// Prop-firm data layer — replaces stocks.ts with the actual product offering
// of FunderPro Futures Challenge. Tiers, platforms, challenges, bot strategies,
// and tier-gated features all live here so the customer portal aligns with
// the marketing site.

import type { ReactNode } from 'react';

export type TierKey = 'Beginner' | 'Mid' | 'Professional';

export interface Tier {
  key: TierKey;
  tagline: string;
  bestFor: string;
  profitSplit: number;       // percent to user
  fundedDaysMin: number;     // days needed before withdrawal
  maxActiveChallenges: number;
  botStrategiesAvailable: number; // 0..4
  maxBotsAtOnce: number;
  hasPrioritySupport: boolean;
  hasCustomRisk: boolean;
  hasAutoFlatten: boolean;
  badge?: string;
  badgeColor: string;
  features: string[];
}

export const TIERS: Record<TierKey, Tier> = {
  Beginner: {
    key: 'Beginner',
    tagline: 'For traders getting started with evaluation accounts',
    bestFor: 'New prop traders',
    profitSplit: 80,
    fundedDaysMin: 5,
    maxActiveChallenges: 1,
    botStrategiesAvailable: 2,            // 2 of 4 strategies
    maxBotsAtOnce: 1,
    hasPrioritySupport: false,
    hasCustomRisk: false,
    hasAutoFlatten: true,
    features: [
      'EOD Balance drawdown',
      'Up to 80% profit split',
      '5 funded days minimum',
      'No activation fee',
    ],
    badgeColor: '#34C759',
  },
  Mid: {
    key: 'Mid',
    tagline: 'A balanced challenge for consistent traders',
    bestFor: 'Intermediate traders',
    profitSplit: 80,
    fundedDaysMin: 5,
    maxActiveChallenges: 3,
    botStrategiesAvailable: 3,            // 3 of 4 strategies
    maxBotsAtOnce: 2,
    hasPrioritySupport: false,
    hasCustomRisk: true,
    hasAutoFlatten: true,
    features: [
      'EOD Balance drawdown',
      'Up to 80% profit split',
      '5 funded days minimum',
      'No activation fee',
      'Custom risk per position',
    ],
    badgeColor: '#007AFF',
  },
  Professional: {
    key: 'Professional',
    tagline: 'Larger accounts with more rules and higher targets',
    bestFor: 'Experienced traders',
    profitSplit: 90,
    fundedDaysMin: 8,
    maxActiveChallenges: 5,
    botStrategiesAvailable: 4,            // all 4 strategies
    maxBotsAtOnce: 3,
    hasPrioritySupport: true,
    hasCustomRisk: true,
    hasAutoFlatten: true,
    features: [
      'EOD Balance drawdown',
      'Up to 90% profit split',
      '8 funded days minimum',
      'No activation fee',
      'Custom risk per position',
      'Priority support',
      'API access',
    ],
    badge: 'Top split',
    badgeColor: '#1C1C1E',
  },
};

// Challenge size + price (matches the marketing)
export interface ChallengeSize {
  size: string;             // "50"  ->  $50K
  price: number;            // USD
  resetPrice: number;
  profitTarget: number;
  maxDrawdown: number;
  dailyDrawdown: number;
  profitShare: number;
  fundedFee: number;
  monthlyFeeIncluded: boolean;
}

export const SIZE_OPTIONS_BY_TIER: Record<TierKey, ChallengeSize[]> = {
  Beginner: [
    { size: '50',  price: 79,  resetPrice: 69,  profitTarget: 3000, maxDrawdown: 2000, dailyDrawdown: 1000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '100', price: 149, resetPrice: 129, profitTarget: 6000, maxDrawdown: 3000, dailyDrawdown: 2000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '150', price: 219, resetPrice: 189, profitTarget: 9000, maxDrawdown: 4500, dailyDrawdown: 3000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
  ],
  Mid: [
    { size: '50',  price: 89,  resetPrice: 79,  profitTarget: 3000, maxDrawdown: 2000, dailyDrawdown: 1000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '100', price: 159, resetPrice: 139, profitTarget: 6000, maxDrawdown: 3000, dailyDrawdown: 2000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '150', price: 229, resetPrice: 199, profitTarget: 9000, maxDrawdown: 4500, dailyDrawdown: 3000, profitShare: 80, fundedFee: 0, monthlyFeeIncluded: true },
  ],
  Professional: [
    { size: '50',  price: 219, resetPrice: 189, profitTarget: 3000, maxDrawdown: 2000, dailyDrawdown: 1000, profitShare: 90, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '100', price: 379, resetPrice: 339, profitTarget: 6000, maxDrawdown: 3000, dailyDrawdown: 2000, profitShare: 90, fundedFee: 0, monthlyFeeIncluded: true },
    { size: '150', price: 539, resetPrice: 489, profitTarget: 9000, maxDrawdown: 4500, dailyDrawdown: 3000, profitShare: 90, fundedFee: 0, monthlyFeeIncluded: true },
  ],
};

// Trading platforms — the 5 official ones from the marketing
export interface TradingPlatform {
  key: string;            // Deepchart
  displayName: string;    // Deepchart®
  description: string;
  bullets: string[];
  tierRequired: TierKey;  // minimum tier to use
  badge?: string;         // e.g. "Most popular"
  icon: string;           // /cloned/wp-content/uploads/platforms/deepchart.svg
  botCompatibility: ('breakout' | 'mean-reversion' | 'trend-follow' | 'sniper')[];
  category: 'charting' | 'order-flow' | 'dom' | 'multi-asset' | 'heatmap';
}

export const PLATFORMS: TradingPlatform[] = [
  {
    key: 'Deepchart',
    displayName: 'Deepchart®',
    description: 'Web & desktop charting with volume profile, TPO and order flow tools.',
    bullets: ['Volume Profile & TPO', 'Advanced DOM', 'Order flow indicators', 'Windows desktop'],
    tierRequired: 'Beginner',
    badge: 'Most popular',
    icon: '/cloned/wp-content/uploads/platforms/deepchart.svg',
    botCompatibility: ['breakout', 'mean-reversion', 'trend-follow', 'sniper'],
    category: 'charting',
  },
  {
    key: 'ATAS',
    displayName: 'ATAS',
    description: 'Order flow analysis, DOM heatmap and cluster charts for futures.',
    bullets: ['Cluster charts', 'DOM heatmap', 'Order flow analysis', 'Multi-broker'],
    tierRequired: 'Beginner',
    icon: '/cloned/wp-content/uploads/platforms/atas.svg',
    botCompatibility: ['mean-reversion', 'sniper'],
    category: 'order-flow',
  },
  {
    key: 'Deepdom',
    displayName: 'Deepdom®',
    description: 'Pure DOM trading with depth-of-market, iceberg detection and footprint charts.',
    bullets: ['DOM trading', 'Iceberg detection', 'Footprint charts', 'Order flow viz'],
    tierRequired: 'Mid',
    icon: '/cloned/wp-content/uploads/platforms/deepdom.svg',
    botCompatibility: ['breakout', 'sniper', 'trend-follow'],
    category: 'dom',
  },
  {
    key: 'Quantower',
    displayName: 'Quantower',
    description: 'Multi-asset analytics with built-in DOM, depth-of-market and trade simulator.',
    bullets: ['Multi-asset analytics', 'Built-in DOM', 'Volume tools', 'Windows / Mac'],
    tierRequired: 'Mid',
    icon: '/cloned/wp-content/uploads/platforms/quantower.svg',
    botCompatibility: ['trend-follow', 'mean-reversion'],
    category: 'multi-asset',
  },
  {
    key: 'Bookmap',
    displayName: 'Bookmap',
    description: 'Heatmap and order book visualization with iceberg and sweep detection.',
    bullets: ['Heatmap', 'Iceberg detection', 'Sweep alerts', 'Historical replay'],
    tierRequired: 'Professional',
    icon: '/cloned/wp-content/uploads/platforms/bookmap.svg',
    botCompatibility: ['sniper', 'mean-reversion'],
    category: 'heatmap',
  },
  // Hidden/extra — only shown in "all platforms" expand
  {
    key: 'Rithmic',
    displayName: 'Rithmic',
    description: 'Direct market access with low-latency execution. Used by pro futures traders.',
    bullets: ['DMA execution', 'Low latency', 'Multi-broker', 'API access'],
    tierRequired: 'Professional',
    icon: '/cloned/wp-content/uploads/platforms/rithmic.svg',
    botCompatibility: ['breakout', 'sniper'],
    category: 'multi-asset',
  },
  {
    key: 'NinjaTrader',
    displayName: 'NinjaTrader',
    description: 'Popular futures platform with advanced charting and automated strategies.',
    bullets: ['Advanced charts', 'Strategy builder', 'Market replay', 'Windows desktop'],
    tierRequired: 'Professional',
    icon: '/cloned/wp-content/uploads/platforms/ninjatrader.svg',
    botCompatibility: ['breakout', 'mean-reversion', 'trend-follow'],
    category: 'multi-asset',
  },
];

// AI Bot Trader — strategies
export type BotStrategy = 'breakout' | 'mean-reversion' | 'trend-follow' | 'sniper';

export interface BotStrategyDef {
  key: BotStrategy;
  name: string;
  tagline: string;
  description: string;
  riskLevel: 'Low' | 'Medium' | 'High';
  bestFor: string;
  pairs: string[];      // instruments it works best on
  tierRequired: TierKey;
  icon: string;         // emoji
  color: string;
}

export const BOT_STRATEGIES: BotStrategyDef[] = [
  {
    key: 'breakout',
    name: 'Breakout',
    tagline: 'Buys breakouts from consolidation zones',
    description: 'Detects price consolidating in a tight range, then enters when price breaks out with volume confirmation. Tight stops, asymmetric reward profile.',
    riskLevel: 'Medium',
    bestFor: 'Trending markets and session opens',
    pairs: ['ES', 'NQ', 'CL', 'GC'],
    tierRequired: 'Beginner',
    icon: '🚀',
    color: '#34C759',
  },
  {
    key: 'mean-reversion',
    name: 'Mean Reversion',
    tagline: 'Fades extensions and buys mean reversion',
    description: 'Identifies when price has overshot a level and reverts to the mean. Works best in range-bound conditions. Wider stops, higher win rate.',
    riskLevel: 'Low',
    bestFor: 'Range-bound markets and reversals',
    pairs: ['ES', 'NQ', 'YM', 'RTY'],
    tierRequired: 'Beginner',
    icon: '↩️',
    color: '#007AFF',
  },
  {
    key: 'trend-follow',
    name: 'Trend Follow',
    tagline: 'Rides the trend with trailing stops',
    description: 'Identifies the dominant trend using multi-timeframe analysis, then enters on pullbacks with trailing stops. Holds winners for big moves.',
    riskLevel: 'Medium',
    bestFor: 'Strong directional markets',
    pairs: ['NQ', 'ES', 'CL', 'GC', 'BTC'],
    tierRequired: 'Mid',
    icon: '📈',
    color: '#FF9500',
  },
  {
    key: 'sniper',
    name: 'Sniper',
    tagline: 'High-precision entries on DOM/heat-map signals',
    description: 'Reads iceberg orders and large bid/ask imbalances. Only enters on high-conviction setups. Best win rate, fewest trades per day.',
    riskLevel: 'High',
    bestFor: 'High-conviction setups and reversals',
    pairs: ['NQ', 'ES', 'BTC'],
    tierRequired: 'Professional',
    icon: '🎯',
    color: '#FF3B30',
  },
];

// Demo bot performance data (would come from /api/apps/.../bot/performance in real flow)
export interface BotInstance {
  id: string;
  strategy: BotStrategy;
  platform: string;       // platform key
  status: 'running' | 'paused' | 'stopped';
  todayPnl: number;
  todayPct: number;
  weekPnl: number;
  weekPct: number;
  tradesToday: number;
  winRate: number;        // 0..1
  openPositions: { pair: string; side: 'long' | 'short'; entry: number; current: number; pnl: number; pnlPct: number }[];
  startedAt: string;      // ISO
  // Optional size/tier reference
  tier: TierKey;
  accountSize: number;
}

export const DEMO_BOT: BotInstance = {
  id: 'bot-demo-1',
  strategy: 'breakout',
  platform: 'Deepchart',
  status: 'running',
  todayPnl: 184.50,
  todayPct: 1.85,
  weekPnl: 1240.00,
  weekPct: 12.4,
  tradesToday: 6,
  winRate: 0.66,
  openPositions: [
    { pair: 'ES',  side: 'long',  entry: 5280.25, current: 5284.50, pnl: 85.00,  pnlPct: 0.40 },
    { pair: 'NQ',  side: 'short', entry: 18420.00, current: 18410.50, pnl: 95.00, pnlPct: 0.05 },
  ],
  startedAt: new Date(Date.now() - 6 * 86400000).toISOString(),
  tier: 'Beginner',
  accountSize: 50000,
};

// Active challenge demo data
export interface ActiveChallenge {
  id: string;
  tier: TierKey;
  size: string;             // "50"  -> $50K
  platform: string;         // platform key
  platformDisplay: string;
  status: 'active' | 'passed' | 'failed' | 'funded';
  progressPct: number;       // 0..100 toward profit target
  profit: number;
  targetProfit: number;
  maxDrawdown: number;
  dailyDrawdown: number;
  daysActive: number;
  daysRemaining: number;    // unlimited for now
  profitShare: number;
  startedAt: string;
  orderId: string;
}

export const DEMO_CHALLENGE: ActiveChallenge = {
  id: 'ch-demo-1',
  tier: 'Beginner',
  size: '50',
  platform: 'Deepchart',
  platformDisplay: 'Deepchart®',
  status: 'active',
  progressPct: 38,
  profit: 1140,
  targetProfit: 3000,
  maxDrawdown: 2000,
  dailyDrawdown: 1000,
  daysActive: 3,
  daysRemaining: 27,
  profitShare: 80,
  startedAt: new Date(Date.now() - 3 * 86400000).toISOString(),
  orderId: 'ord-1',
};

// Tier upgrade comparison — what each tier unlocks
export interface TierFeature {
  name: string;
  beginner: string | boolean;
  mid: string | boolean;
  professional: string | boolean;
}

export const TIER_COMPARISON: TierFeature[] = [
  { name: 'Profit split',          beginner: 'Up to 80%',  mid: 'Up to 80%',  professional: 'Up to 90%' },
  { name: 'Funded days minimum',   beginner: '5',           mid: '5',           professional: '8' },
  { name: 'Active challenges',     beginner: '1',           mid: '3',           professional: '5' },
  { name: 'AI bot strategies',     beginner: '2 of 4',      mid: '3 of 4',      professional: 'All 4' },
  { name: 'Bots at once',          beginner: '1',           mid: '2',           professional: '3' },
  { name: 'Custom risk per pos',   beginner: false,         mid: true,          professional: true },
  { name: 'Priority support',      beginner: false,         mid: false,         professional: true },
  { name: 'API access',            beginner: false,         mid: false,         professional: true },
  { name: 'Bookmap & Rithmic',     beginner: false,         mid: false,         professional: true },
  { name: 'Position auto-flatten', beginner: true,          mid: true,          professional: true },
];

// Services shown on dashboard (matches the marketing's "Services" section)
export const SERVICES = [
  { key: 'wallet',     label: 'Wallet',     icon: 'wallet' },
  { key: 'platforms',  label: 'Platforms',  icon: 'layers' },
  { key: 'calendar',   label: 'Calendar',   icon: 'calendar' },
  { key: 'affiliate',  label: 'Affiliate',  icon: 'gift' },
];

// Find helpers
export function platformFor(key: string): TradingPlatform | undefined {
  return PLATFORMS.find((p) => p.key === key);
}

export function tierFor(key: TierKey): Tier {
  return TIERS[key];
}

export function sizeFor(tier: TierKey, size: string): ChallengeSize | undefined {
  return SIZE_OPTIONS_BY_TIER[tier]?.find((s) => s.size === size);
}

export function strategyFor(key: BotStrategy): BotStrategyDef | undefined {
  return BOT_STRATEGIES.find((s) => s.key === key);
}

// Tier comparison for the picker — returns list of tiers in order
export const TIER_ORDER: TierKey[] = ['Beginner', 'Mid', 'Professional'];
